import {
  ChangeDetectionStrategy,
  Component,
  DEFAULT_CURRENCY_CODE,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  LOCALE_ID,
  OnInit,
  Output
} from '@angular/core';
import { formatCurrency, getCurrencySymbol } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ButtonTypeEnum } from '../../../../../../ui/buttons/button/button-type.enum';
import { VariableBenefitConfigurationPaymentFormGroup } from '../../form-groups/variable-benefit-configuration-payment-form-group';
import { VariableBenefitPaymentFrequencyEnum } from '../../../../../../models/enum/shared/variable-benefit-payment-frequency.enum';
import { Alert } from '../../../../../../models/alerts/dto/alert';
import { buildVbTargetSplitWarningBanner, VbTargetSplitWarnings } from '../../utils/vb-target-split-warning-utils';

@Component({
  selector: 'app-vb-target-payment',
  templateUrl: './vb-target-payment.component.html',
  styleUrls: ['./vb-target-payment.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class VbTargetPaymentComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private currencyCode = inject(DEFAULT_CURRENCY_CODE);
  private locale = inject(LOCALE_ID);

  @Input({ required: true }) form!: VariableBenefitConfigurationPaymentFormGroup;
  @Input() combinedMinimum = 0;
  @Input() combinedMaximum = 0;
  @Input() isCalculating = false;
  @Input() formSubmitted = false;
  @Input() set refusal(refusal: string | null) {
    this.customErrorMessages = { ...this.customErrorMessages, targetPaymentRefusal: refusal ?? '' };
  }
  @Input() set warnings(warnings: VbTargetSplitWarnings | null) {
    this.warningBanner = warnings
      ? buildVbTargetSplitWarningBanner(warnings, amount => this.formatAmount(amount))
      : null;
  }
  @Output() calculateClicked = new EventEmitter<void>();

  public calculateAttempted = false;
  public warningBanner: Alert | null = null;

  public customErrorMessages = {
    required: $localize`Enter an amount to use the calculator`,
    targetPaymentPositive: $localize`A target payment must be a positive amount.`,
    targetPaymentRefusal: ''
  };

  public ngOnInit(): void {
    this.form.controls.targetRecurringPaymentAmount.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => (this.calculateAttempted = false));
  }

  public onCalculateClicked(): void {
    this.calculateAttempted = true;
    this.calculateClicked.emit();
  }

  private formatAmount(amount: number): string {
    return formatCurrency(
      amount,
      this.locale,
      getCurrencySymbol(this.currencyCode, 'wide', this.locale),
      this.currencyCode
    );
  }

  protected readonly ButtonTypeEnum = ButtonTypeEnum;
  protected readonly VariableBenefitPaymentFrequencyEnum = VariableBenefitPaymentFrequencyEnum;
}
